import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class EncryptionService {
  private readonly algorithm = 'AES-GCM';
  private readonly keyLength = 256;
  private readonly ivLength = 12; // 96 bits for GCM
  private readonly tagLength = 128; // 128 bits for GCM

  /**
   * Generate or retrieve encryption key
   * In production, you might want to derive this from a user-specific secret
   */
  private async getKey(): Promise<CryptoKey> {
    // Generate a key from a fixed password (in production, use a secure key management system)
    // For now, we'll use a derivation from a constant - in production, this should be more secure
    const password = 'HCMS_ENCRYPTION_KEY_2024'; // Replace with secure key management
    const encoder = new TextEncoder();
    const passwordData = encoder.encode(password);

    // Import key using PBKDF2
    const baseKey = await crypto.subtle.importKey(
      'raw',
      passwordData,
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    // Derive the actual encryption key
    const salt = encoder.encode('HCMS_SALT'); // In production, use a random salt
    const keyMaterial = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256'
      },
      baseKey,
      {
        name: this.algorithm,
        length: this.keyLength
      },
      false,
      ['encrypt', 'decrypt']
    );

    return keyMaterial;
  }

  /**
   * Encrypt a string value
   * @param plaintext The text to encrypt
   * @returns Encrypted string (base64 encoded)
   */
  async encrypt(plaintext: string): Promise<string> {
    try {
      const key = await this.getKey();
      const encoder = new TextEncoder();
      const data = encoder.encode(plaintext);

      // Generate a random IV for each encryption
      const iv = crypto.getRandomValues(new Uint8Array(this.ivLength));

      // Encrypt the data
      const encryptedData = await crypto.subtle.encrypt(
        {
          name: this.algorithm,
          iv: iv,
          tagLength: this.tagLength
        },
        key,
        data
      );

      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encryptedData.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(encryptedData), iv.length);

      // Convert to base64 for storage/transmission
      return this.arrayBufferToBase64(combined.buffer);
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypt an encrypted string
   * @param ciphertext The encrypted text (base64 encoded)
   * @returns Decrypted string
   */
  async decrypt(ciphertext: string): Promise<string> {
    try {
      const key = await this.getKey();
      
      // Convert base64 to ArrayBuffer
      const combined = this.base64ToArrayBuffer(ciphertext);
      
      // Extract IV and encrypted data
      const iv = combined.slice(0, this.ivLength);
      const encryptedData = combined.slice(this.ivLength);

      // Decrypt the data
      const decryptedData = await crypto.subtle.decrypt(
        {
          name: this.algorithm,
          iv: iv,
          tagLength: this.tagLength
        },
        key,
        encryptedData
      );

      // Convert to string
      const decoder = new TextDecoder();
      return decoder.decode(decryptedData);
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt data');
    }
  }

  /**
   * Encrypt an object (converts to JSON first)
   * @param obj The object to encrypt
   * @returns Encrypted string (base64 encoded)
   */
  async encryptObject<T>(obj: T): Promise<string> {
    const jsonString = JSON.stringify(obj);
    return this.encrypt(jsonString);
  }

  /**
   * Decrypt an encrypted object
   * @param ciphertext The encrypted text (base64 encoded)
   * @returns Decrypted object
   */
  async decryptObject<T>(ciphertext: string): Promise<T> {
    const decryptedString = await this.decrypt(ciphertext);
    return JSON.parse(decryptedString) as T;
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Convert base64 string to ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}


